import {omit} from 'lodash';
import {useCallback} from 'react';

import {HandlerOf} from '../../../libs/shared/helpers/src/react/reactHelpers';
import {
  DataFieldsTableColumnModel,
  makeDataFieldsColumnId,
} from '../../models/api/dataFieldsTableColumnModel';
import {DataFieldsTableData, DataFieldsTableModel} from '../../models/api/dataFieldsTableModel';
import {DataFieldsTableProps} from './dataFieldsTable';
import {isValidDataFieldsTableValueForType} from './helpers';

export type DataFieldsTableUpdater = (initialTable: DataFieldsTableModel) => DataFieldsTableModel | undefined;

/**
 * Hook to abstract the most common use cases for table updaters.
 */
export function useDataFieldsTableActions(onUpdateTable: HandlerOf<DataFieldsTableUpdater>) {
  const onUpdateCell = useCallback<DataFieldsTableProps['onUpdateCell']>(
    (rowIndex, column, value) =>
      onUpdateTable((current) => ({
        columns: current.columns,
        rows: current.rows.map((row, index) => {
          if (index !== rowIndex) {
            return row;
          }

          return {
            ...row,
            [column.id]: value,
          };
        }),
      })),
    [onUpdateTable],
  );

  const onAddRow = useCallback(
    () => onUpdateTable((current) => ({...current, rows: [...current.rows, {}]})),
    [onUpdateTable],
  );

  const onAddColumn = useCallback<DataFieldsTableProps['onAddColumn']>(
    (data) =>
      onUpdateTable((current) => ({
        ...current,
        columns: [...current.columns, {...data, id: makeDataFieldsColumnId(current.columns)}],
      })),
    [onUpdateTable],
  );

  const onEditColumn = useCallback<DataFieldsTableProps['onEditColumn']>(
    (id, newColumnDefinition) =>
      onUpdateTable((current) => {
        const hasTypeChange = current.columns.find((col) => col.id === id)?.type !== newColumnDefinition.type;

        const newColumns = current.columns.map((column) => {
          if (column.id !== id) {
            return column;
          }
          return {
            ...column,
            ...newColumnDefinition,
          };
        });

        const cleanRows = hasTypeChange
          ? removeInvalidEntries(current.rows, {id, ...newColumnDefinition})
          : current.rows;

        return {
          ...current,
          rows: cleanRows,
          columns: newColumns,
        };
      }),
    [onUpdateTable],
  );

  const onDeleteColumn = useCallback<DataFieldsTableProps['onDeleteColumn']>(
    (id) =>
      onUpdateTable((current) => ({
        ...current,
        columns: current.columns.filter((column) => column.id !== id),
        rows: current.rows.map((row) => omit(row, id)),
      })),
    [onUpdateTable],
  );

  const onDeleteRow = useCallback<DataFieldsTableProps['onDeleteRow']>(
    (removeIndex) =>
      onUpdateTable((current) => ({
        ...current,
        rows: current.rows.filter((_, index) => index !== removeIndex),
      })),
    [onUpdateTable],
  );

  return {
    onUpdateCell,
    onAddRow,
    onAddColumn,
    onEditColumn,
    onDeleteColumn,
    onDeleteRow,
  };
}

function removeInvalidEntries(rows: Array<DataFieldsTableData>, column: Partial<DataFieldsTableColumnModel>) {
  const {type, id} = column;
  if (!type || !id) {
    return rows;
  }

  return rows.map((row) => {
    if (!isValidDataFieldsTableValueForType(type, row[id])) {
      return {...row, [id]: undefined};
    }
    return row;
  });
}
